import { Pin, PinOff, X } from 'lucide-react'
import { useToast } from '@/context/ToastContext'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/Button'

/** 4xx → amber border/dot only; 5xx → theme `red` border + dot (tailwind.config overrides default red-* scale); no status → slate */
function httpErrorTone(status: number | null | undefined): 'client' | 'server' | 'neutral' {
  if (status == null) return 'neutral'
  if (status >= 400 && status < 500) return 'client'
  if (status >= 500) return 'server'
  return 'neutral'
}

export function ToastContainer() {
  const { toasts, dismissToast, toggleToastPin } = useToast()
  return (
    <div className="fixed bottom-5 right-5 z-50 flex flex-col gap-2">
      {toasts.map(t => {
        const structured = t.detail !== undefined
        const pinned = t.pinned === true
        const tone = structured && t.type === 'error' ? httpErrorTone(t.statusCode ?? null) : null

        return (
          <div
            key={t.id}
            className={cn(
              'flex max-w-[min(100vw-2rem,28rem)] items-start gap-2 rounded-lg border bg-surface py-2.5 pl-3 pr-1 text-xs shadow-xl animate-in slide-in-from-bottom-2',
              t.type === 'success' && 'border-green/30',
              t.type === 'info' && 'border-accent/30',
              t.type === 'error' && !structured && 'border-red/50',
              tone === 'client' && 'border-amber-500/45',
              tone === 'server' && 'border-red/50',
              tone === 'neutral' && structured && 'border-slate-500/35',
              pinned &&
                (tone === 'client'
                  ? 'ring-1 ring-amber-400/45'
                  : tone === 'server' || !structured
                    ? 'ring-1 ring-red/45'
                    : 'ring-1 ring-slate-400/35'),
            )}
          >
            <span
              className={cn(
                'mt-1.5 h-2 w-2 flex-shrink-0 rounded-full',
                t.type === 'success' && 'bg-green',
                t.type === 'info' && 'bg-accent',
                t.type === 'error' && !structured && 'bg-red',
                tone === 'client' && 'bg-amber-500',
                tone === 'server' && 'bg-red',
                tone === 'neutral' && structured && 'bg-slate-400',
              )}
            />
            <div className="min-w-0 flex-1 py-0.5">
              {structured ? (
                <div className="flex flex-col gap-1.5 text-left">
                  {t.title ? <span className="text-xs font-semibold text-ink">{t.title}</span> : null}
                  {t.statusCode != null ? (
                    <span
                      className={cn(
                        'font-mono text-[10px] font-medium tabular-nums',
                        tone === 'client' && 'text-muted',
                        tone === 'server' && 'text-red-700/90 dark:text-red-300/80',
                        tone === 'neutral' && 'text-muted',
                      )}
                    >
                      HTTP {t.statusCode}
                    </span>
                  ) : null}
                  {t.problemTitle ? (
                    <span className="text-xs font-medium text-ink">{t.problemTitle}</span>
                  ) : null}
                  <span className="font-mono text-xs leading-relaxed text-ink break-words whitespace-pre-wrap">
                    {t.detail}
                  </span>
                </div>
              ) : (
                <span className="text-left font-mono text-ink break-words whitespace-pre-wrap">{t.message}</span>
              )}
            </div>
            <div className="flex flex-shrink-0 flex-col gap-0.5 pr-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 text-muted hover:text-accent"
                onClick={() => toggleToastPin(t.id)}
                title={pinned ? 'Unpin (auto-dismiss when idle)' : 'Pin (keep open)'}
                aria-label={pinned ? 'Unpin toast' : 'Pin toast'}
              >
                {pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 text-muted hover:text-ink"
                onClick={() => dismissToast(t.id)}
                title="Dismiss"
                aria-label="Dismiss toast"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )
      })}
    </div>
  )
}
